import React, { useState } from 'react';
import { Archive, Copy, CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import Tooltip from '../common/Tooltip';

/**
 * Queue Write output structure from backend
 */
export interface QueueWriteOutput {
  success: boolean;
  items_written: number;
  queue_id: number;
  queue_name: string;
  write_mode: 'single' | 'array';
  item_ids: string[];
  /** Payloads that were written, parallel to item_ids. Populated for runs after the change that added it. */
  items_data?: unknown[];
  error?: string;
}

/**
 * Type guard to check if output is a valid QueueWriteOutput
 */
export function isQueueWriteOutput(output: any): output is QueueWriteOutput {
  return (
    output &&
    typeof output === 'object' &&
    'success' in output &&
    'queue_id' in output &&
    ('items_written' in output || 'error' in output)
  );
}

interface QueueWriteOutputRendererProps {
  output: QueueWriteOutput;
  onCopy?: (text: string) => void;
}

/**
 * Custom renderer for queue_write step output.
 * Displays a formatted view of queue write results including:
 * - Success/failure status
 * - Summary cards (items written, queue name, write mode)
 * - List of written item IDs with copy functionality
 */
const QueueWriteOutputRenderer: React.FC<QueueWriteOutputRendererProps> = ({ output, onCopy }) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const handleCopy = (text: string) => {
    if (onCopy) {
      onCopy(text);
    } else {
      navigator.clipboard.writeText(text);
    }
  };

  const toggleItem = (index: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  /**
   * Return a single-line summary suitable for a table row. Prefer a
   * meaningful string field from the payload when possible.
   */
  const summarizePayload = (payload: unknown): string => {
    if (payload === null || payload === undefined) return '';
    if (typeof payload !== 'object') return String(payload);
    // Prefer the first string value from the object for a compact summary
    const entries = Object.entries(payload as Record<string, unknown>);
    for (const [key, value] of entries) {
      if (typeof value === 'string' && value.trim()) {
        return entries.length === 1 ? value : `${key}: ${value}`;
      }
    }
    return JSON.stringify(payload);
  };

  const isSuccess = output.success;
  const hasPayloads = Array.isArray(output.items_data) && output.items_data.length > 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className={`${isSuccess ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'} border rounded-lg p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 ${isSuccess ? 'bg-teal-100 dark:bg-teal-900/30 border-teal-200 dark:border-teal-800' : 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800'} rounded-full flex items-center justify-center border`}>
              <Archive className={`w-5 h-5 ${isSuccess ? 'text-teal-600' : 'text-red-600'}`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Queue Write Results</h3>
              <div className="flex items-center space-x-2 mt-0.5">
                {isSuccess ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-teal-600" />
                    <span className="text-sm font-medium text-teal-600">Write Successful</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 text-red-600" />
                    <span className="text-sm font-medium text-red-600">Write Failed</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Items Written</p>
              <p className="text-xl font-bold text-gray-900 dark:text-warm-white">{output.items_written || 0}</p>
            </div>
            <div className={`w-8 h-8 ${isSuccess ? 'bg-teal-100 dark:bg-teal-900/30' : 'bg-gray-100 dark:bg-gray-700'} rounded-full flex items-center justify-center`}>
              <Archive className={`w-4 h-4 ${isSuccess ? 'text-teal-600' : 'text-gray-400'}`} />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Queue</p>
            <Tooltip content={output.queue_name}>
            <p className="text-sm font-semibold text-gray-900 dark:text-warm-white truncate">
              {output.queue_name}
            </p>
            </Tooltip>
            <p className="text-xs text-gray-400">ID: {output.queue_id}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Write Mode</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-warm-white capitalize">{output.write_mode}</p>
            <p className="text-xs text-gray-400">
              {output.write_mode === 'array' ? 'Multiple items per write' : 'One item per write'}
            </p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {output.error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-red-800 dark:text-red-300">Error</h4>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">{output.error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Written Items List - shows IDs and payloads when available */}
      {output.item_ids && output.item_ids.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Written Items ({output.item_ids.length})
            </h4>
            <button
              onClick={() => handleCopy(output.item_ids.join('\n'))}
              className="inline-flex items-center space-x-1 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
            >
              <Copy className="w-3 h-3" />
              <span>Copy All IDs</span>
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {output.item_ids.map((itemId, index) => {
                const payload = hasPayloads ? output.items_data![index] : undefined;
                const isExpanded = expandedItems.has(index);
                const summary = payload !== undefined ? summarizePayload(payload) : null;

                return (
                  <li key={itemId || index} className="bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <div className="px-4 py-2 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {hasPayloads && payload !== undefined && typeof payload === 'object' ? (
                          <button
                            onClick={() => toggleItem(index)}
                            className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-3 h-3" />
                            ) : (
                              <ChevronRight className="w-3 h-3" />
                            )}
                          </button>
                        ) : (
                          <span className="w-3 h-3 flex-shrink-0" />
                        )}
                        <span className="text-sm font-mono text-teal-600 dark:text-teal-400 flex-shrink-0">
                          #{itemId}
                        </span>
                        {summary && (
                          <span className="text-sm text-gray-600 dark:text-gray-400 truncate">
                            {summary}
                          </span>
                        )}
                      </div>
                      <Tooltip content="Copy ID">
                        <button
                          onClick={() => handleCopy(itemId)}
                          className="p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex-shrink-0"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </Tooltip>
                    </div>
                    {isExpanded && payload !== undefined && (
                      <div className="px-4 pb-3">
                        <div className="flex items-start justify-between gap-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-2">
                          <pre className="text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words flex-1 min-w-0">
                            {typeof payload === 'string'
                              ? payload
                              : JSON.stringify(payload, null, 2)}
                          </pre>
                          <Tooltip content="Copy payload">
                            <button
                              onClick={() =>
                                handleCopy(
                                  typeof payload === 'string'
                                    ? payload
                                    : JSON.stringify(payload, null, 2)
                                )
                              }
                              className="p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 flex-shrink-0"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* No Items Written */}
      {isSuccess && (!output.item_ids || output.item_ids.length === 0) && (
        <div className="text-center py-6 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
          <Archive className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No item IDs recorded</p>
        </div>
      )}
    </div>
  );
};

export default QueueWriteOutputRenderer;
